import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Octagon,
  Triangle,
  Circle,
  Square,
  RoadHorizon,
  SpinnerGap
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RoadSignsPage = () => {
  const [signs, setSigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const fetchSigns = async () => {
      try {
        const { data } = await axios.get(`${API}/road-signs`, { withCredentials: true });
        setSigns(data);
        if (data.length > 0) {
          setSelectedCategory(data[0].category);
        }
      } catch (error) {
        console.error('Failed to fetch road signs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSigns();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const currentCategory = signs.find(s => s.category === selectedCategory);

  const getShapeIcon = (shape) => {
    switch (shape?.toLowerCase()) {
      case 'octagon': return Octagon;
      case 'triangle': return Triangle;
      case 'circle': return Circle;
      case 'square':
      case 'rectangle': return Square;
      default: return RoadHorizon;
    }
  };

  const getCategoryColor = (category) => {
    if (category?.includes('Regulatory')) return 'text-primary';
    if (category?.includes('Warning')) return 'text-amber-500';
    if (category?.includes('Information')) return 'text-blue-500';
    return 'text-muted-foreground';
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="road-signs-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Reference Guide
          </p>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
            Road Signs
          </h1>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {signs.map((cat) => (
            <button
              key={cat.category}
              onClick={() => setSelectedCategory(cat.category)}
              className={cn(
                "px-4 py-2 rounded-sm text-sm font-medium transition-colors",
                selectedCategory === cat.category
                  ? "bg-primary text-primary-foreground"
                  : "bg-card border border-border hover:bg-muted"
              )}
              data-testid={`category-tab-${cat.category}`}
            >
              {cat.category}
            </button>
          ))}
        </div>

        {/* Signs Grid */}
        {currentCategory && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentCategory.signs.map((sign, index) => {
              const ShapeIcon = getShapeIcon(sign.shape);
              return (
                <div
                  key={index}
                  className="bg-card border border-border rounded-sm p-6 card-hover"
                  data-testid={`sign-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "w-14 h-14 rounded-sm flex items-center justify-center flex-shrink-0 bg-muted",
                      getCategoryColor(currentCategory.category)
                    )}>
                      <ShapeIcon weight="fill" className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-lg mb-1">{sign.name}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{sign.description}</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs px-2 py-1 bg-muted rounded-sm">
                          {sign.shape}
                        </span>
                        <span className="text-xs px-2 py-1 bg-muted rounded-sm">
                          {sign.color}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-12 bg-card border border-border rounded-sm p-6">
          <h2 className="font-heading font-bold text-xl mb-4">Understanding Road Signs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Circle weight="fill" className="w-6 h-6 text-primary" />
                <span className="font-bold">Circular Signs</span>
              </div>
              <p className="text-sm text-muted-foreground">
                With red borders give orders (prohibitions). Blue circles give positive instructions.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Triangle weight="fill" className="w-6 h-6 text-amber-500" />
                <span className="font-bold">Triangular Signs</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Warn of hazards ahead. The point faces upward.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Square weight="fill" className="w-6 h-6 text-blue-500" />
                <span className="font-bold">Rectangular Signs</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Provide information. Blue for motorways, green for primary routes, white for local.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
